import { useLayoutEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { getReportAnswers, getSurvivorAndReportById } from "actions/reportActions";
import type { ReportData } from "actions/reportActions";
import { getReportNotes } from "actions/noteActions";
import { useUserState } from "context";
import {
  initialReportUserNotesAnswersState,
  ReportUserNotesAnswers,
  type ApiAnswer,
  type Answer,
} from "context/reportUserNotesAnswers";
import { useApiAnswersToAnswersMap } from "./useApiAnswersToAnswersMap";
import { useLocation } from "react-router-dom";
import { UserInfo } from "context/user";
import { useHasPermission } from "./useHasPermission";
import { Permissions, RoleType } from "actions/roleActions";

export type UsePopulateReportUserNotesAnswersOptions = {
  reportId: string | number | null | undefined;
  getNotes: boolean;
  /** When false, the query does not run automatically (e.g. until another observer enables it). Default true. */
  enabled?: boolean;
  /** Remove cached queries for this report when the route report id changes. Default false. */
  resetBeforePopulate?: boolean;
};

export type PopulateReportUserNotesAnswersResult = {
  reportUserNotesAnswers: ReportUserNotesAnswers;
  data: ReportUserNotesAnswers | undefined;
  refetch: UseQueryResult<ReportUserNotesAnswers>["refetch"];
  isPending: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: UseQueryResult<ReportUserNotesAnswers>["error"];
  status: UseQueryResult<ReportUserNotesAnswers>["status"];
  fetchStatus: UseQueryResult<ReportUserNotesAnswers>["fetchStatus"];
};

type HasPermissionFn = (
  permission: Permissions,
  report: ReportData | undefined,
  user?: UserInfo & RoleType
) => boolean;

export const reportUserNotesAnswersPopulateQueryKey = (
  reportId: string | number,
  getNotes: boolean,
  navReportId: number | "no-nav"
) =>
  ["reportUserNotesAnswersPopulate", String(reportId), getNotes, navReportId] as const;

/** Whether cached query data is missing or for a different report than `reportId`. */
export const useReportPopulateNeedsFreshData = (
  reportId: string | null | undefined,
  getNotes: boolean
): boolean => {
  const queryClient = useQueryClient();
  const location = useLocation() as { state?: { report?: ReportUserNotesAnswers["report"] } };
  const reportFromNav = location.state?.report;
  const navReportId =
    reportFromNav?.id !== undefined && reportFromNav.id !== null
      ? Number(reportFromNav.id)
      : ("no-nav" as const);

  const rk =
    reportId != null && reportId !== "" ? String(reportId) : null;
  if (!rk) {
    return false;
  }
  const key = reportUserNotesAnswersPopulateQueryKey(rk, getNotes, navReportId);
  const cached = queryClient.getQueryData<ReportUserNotesAnswers>(key);
  return !cached?.report?.id || Number(reportId) !== cached.report.id;
};

async function fetchReportUserNotesAnswersPayload(params: {
  reportId: string | number;
  getNotes: boolean;
  reportFromNav: ReportUserNotesAnswers["report"] | undefined;
  userInfo: UserInfo & RoleType;
  hasPermission: HasPermissionFn;
  apiAnswersToAnswersMap: (apiAnswers: Array<ApiAnswer>) => Map<string, Answer>;
}): Promise<ReportUserNotesAnswers> {
  const { reportId, getNotes, reportFromNav, userInfo, hasPermission, apiAnswersToAnswersMap } =
    params;

  const reportUserNotesAnswers: ReportUserNotesAnswers = {};

  try {
    if (reportFromNav && reportFromNav.id === Number(reportId)) {
      reportUserNotesAnswers.report = reportFromNav;
    } else {
      const reportResult = await getSurvivorAndReportById(reportId);
      reportUserNotesAnswers.report = reportResult?.data;
      reportUserNotesAnswers.reportUser = reportResult?.data;

      if (
        hasPermission(Permissions.ShareOrganization, reportUserNotesAnswers.report) &&
        userInfo.organizationId
      ) {
        const updatedAssignedOrganizations = [
          ...(reportUserNotesAnswers.report?.assignedOrganizations || []),
          {
            id: Number(userInfo.organizationId),
            name: userInfo.organizationName,
            type: userInfo.organizationTypeId,
          },
        ];
        reportUserNotesAnswers.report = {
          ...reportUserNotesAnswers.report,
          assignedOrganizations: updatedAssignedOrganizations,
        };
      }
    }

    try {
      const answersResult = await getReportAnswers(reportId);
      reportUserNotesAnswers.answers = apiAnswersToAnswersMap(answersResult.data);
    } catch {
      reportUserNotesAnswers.answers = apiAnswersToAnswersMap([]);
    }

    if (getNotes) {
      try {
        const notesResult = await getReportNotes(reportId);
        reportUserNotesAnswers.notes = notesResult.data;
      } catch {
        reportUserNotesAnswers.notes = [];
      }
    }
  } catch (err) {
    // TODO: https://trello.com/c/D2oyeSVv/391-error-handling-updates
    console.log(err);
  }

  return reportUserNotesAnswers;
}

export const usePopulateReportUserNotesAnswers = ({
  reportId,
  getNotes,
  enabled = true,
  resetBeforePopulate = false,
}: UsePopulateReportUserNotesAnswersOptions): PopulateReportUserNotesAnswersResult => {
  const userInfo: UserInfo & RoleType = useUserState();
  const apiAnswersToAnswersMap = useApiAnswersToAnswersMap();
  const hasPermission = useHasPermission();
  const queryClient = useQueryClient();
  const location = useLocation() as { state?: { report?: ReportUserNotesAnswers["report"] } };

  const reportFromNav = location.state?.report;
  const navReportId =
    reportFromNav?.id !== undefined && reportFromNav.id !== null
      ? Number(reportFromNav.id)
      : ("no-nav" as const);

  const reportKey =
    reportId != null && reportId !== "" ? String(reportId) : null;
  const queryEnabled = Boolean(enabled && reportKey);

  const prevReportKeyRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!resetBeforePopulate || !enabled || !reportKey) {
      return;
    }
    if (prevReportKeyRef.current !== reportKey) {
      queryClient.removeQueries({
        queryKey: ["reportUserNotesAnswersPopulate", reportKey],
        exact: false,
      });
      prevReportKeyRef.current = reportKey;
    }
  }, [resetBeforePopulate, enabled, reportKey, queryClient]);

  const query = useQuery({
    queryKey: reportKey
      ? reportUserNotesAnswersPopulateQueryKey(reportKey, getNotes, navReportId)
      : (["reportUserNotesAnswersPopulate", "disabled"] as const),
    queryFn: () => {
      if (reportId == null || reportId === "") {
        return Promise.resolve({});
      }
      return fetchReportUserNotesAnswersPayload({
        reportId,
        getNotes,
        reportFromNav,
        userInfo,
        hasPermission: hasPermission as HasPermissionFn,
        apiAnswersToAnswersMap,
      });
    },
    enabled: queryEnabled,
  });

  const data = query.data;
  const reportUserNotesAnswers = data ?? initialReportUserNotesAnswersState;

  return {
    reportUserNotesAnswers,
    data,
    refetch: query.refetch,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
    status: query.status,
    fetchStatus: query.fetchStatus,
  };
};
